import { useCallback, useRef, useState, type DragEvent } from "react";
import { TFunction } from "i18next";
import { CDTAndCustomProcedureValue } from "../../../components/CDTAndCustomProcedureSelect";
import {
  treatmentPlanService,
  TreatmentPlanResponse,
  AppointmentUpdateData,
  StepUpdateData,
  TreatmentPlanAppointment,
} from "../../../services/treatmentPlanService";
import { CDTCode } from "../../../services/cdtCodesService";
import {
  EditableAppointmentData,
  EditableProcedureData,
} from "../../../types/editableTreatmentPlan";
import {
  bundleMemberCodes,
  codeSuggestionsForPayload,
  convertToEditableAppointment,
  resolveTreatAreaCapabilitiesForCodes,
  toothSurfacesFromProcedure,
} from "../../../utils/treatmentPlanAppointmentUtils";
import {
  cloneEditableAppointment,
  computeAppointmentDuration,
  hasAppointmentDraftChanges,
  procedureAfterCdtCodesCleared,
  restoreAppointmentFromInitialDraft,
  sanitizeFeeInput,
  syncProcedureAfterCdtChange,
  syncProcedureIsFeeEdited,
} from "../../../utils/treatmentPlanDraftUtils";
import {
  saveTreatmentPlanUpdate,
} from "../../../utils/treatmentPlanSaveUtils";
import {
  isCustomProcedureError,
  parseTreatmentPlanApiError,
} from "../../../utils/treatmentPlanApiErrors";
import {
  applyToothMultiplier,
  clearAreaSurfaces,
  filterSurfacesForCapabilities,
  hasPureAreaSelection,
  keepOnlyAreaSurfaces,
  scaleFeeAndDurationForTeeth,
} from "../../../utils/toothSurfacesUtils";
import {
  canSelectSurfaces,
  canSelectTeeth,
} from "../../../utils/treatAreaCapabilities";
import { peekOrganizationCdtCodes } from "../../../utils/organizationProceduresCache";

type NotificationSeverity = "success" | "error";

interface UseTreatmentPlanAppointmentsParams {
  treatmentPlan: TreatmentPlanResponse | null;
  setTreatmentPlan: React.Dispatch<
    React.SetStateAction<TreatmentPlanResponse | null>
  >;
  planId: string | undefined;
  cdtCodes: CDTCode[];
  canEditAppointments: boolean;
  showNotification: (
    message: string,
    severity?: NotificationSeverity,
  ) => void;
  onRefetchPlan: (force?: boolean) => Promise<unknown>;
  t: TFunction;
}

export function useTreatmentPlanAppointments({
  treatmentPlan,
  setTreatmentPlan,
  planId,
  cdtCodes,
  canEditAppointments,
  showNotification,
  onRefetchPlan,
  t,
}: UseTreatmentPlanAppointmentsParams) {
  const [appointments, setAppointments] = useState<TreatmentPlanAppointment[]>(
    [],
  );
  const [editingAppointmentId, setEditingAppointmentId] = useState<
    number | null
  >(null);
  const [appointmentDrafts, setAppointmentDrafts] = useState<
    Record<number, EditableAppointmentData>
  >({});
  const [appointmentInitialDrafts, setAppointmentInitialDrafts] = useState<
    Record<number, EditableAppointmentData>
  >({});
  const [savingAppointmentId, setSavingAppointmentId] = useState<number | null>(
    null,
  );
  const [deletingAppointmentId, setDeletingAppointmentId] = useState<
    number | null
  >(null);
  const [draggingAppointmentIndex, setDraggingAppointmentIndex] = useState<
    number | null
  >(null);
  const [dragOverAppointmentIndex, setDragOverAppointmentIndex] = useState<
    number | null
  >(null);
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const appointmentDraftsRef = useRef(appointmentDrafts);
  appointmentDraftsRef.current = appointmentDrafts;

  /** Shows a rejected step field (e.g. a stale Custom Procedure link) instead of a generic failure. */
  const notifySaveError = useCallback(
    (saveError: unknown) => {
      const stepError = parseTreatmentPlanApiError(saveError).stepErrors[0];
      showNotification(
        stepError
          ? isCustomProcedureError(stepError)
            ? t("editTreatmentPlan.customProcedureNotFound")
            : stepError.message
          : t("editTreatmentPlan.failedToSave"),
        "error",
      );
    },
    [showNotification, t],
  );

  const syncAppointmentsFromPlan = useCallback((plan: TreatmentPlanResponse) => {
    setAppointments(
      [...plan.appointments].sort((a, b) => a.sortOrder - b.sortOrder),
    );
  }, []);

  const hasActiveEditSessions = editingAppointmentId !== null;
  const isDeletingAppointment = deletingAppointmentId !== null;
  const isAppointmentInteractionBlocked =
    hasActiveEditSessions || isDeletingAppointment;

  const sortedAppointments = [...appointments].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

  const hasOptions = appointments.some(
    (appt) => appt.conditionalPath && appt.conditionalPath.trim() !== "",
  );

  const updatePlanAppointments = useCallback(
    async (nextAppointments: TreatmentPlanAppointment[]) => {
      if (!treatmentPlan || !planId) return;

      const sortedNext = [...nextAppointments].map((appointment, index) => ({
        ...appointment,
        sortOrder: index + 1,
      }));

      const appointmentPayload = sortedNext.map((appointment, index) => {
        const payload: AppointmentUpdateData = {
          timeframeLabel:
            index < sortedNext.length - 1
              ? appointment.timeframeLabel || ""
              : "",
          conditionalPath: appointment.conditionalPath || undefined,
          description: appointment.description || undefined,
          sortOrder: index + 1,
          totalDurationMinutes: appointment.totalDurationMinutes,
          steps: [...appointment.steps]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((step, stepIndex) => {
              const stepPayload: StepUpdateData = {
                name: step.name,
                description: step.description || undefined,
                sortOrder: stepIndex + 1,
                durationMinutes: step.durationMinutes,
                fee: step.fee,
                // Sent explicitly — omitting it unlinks the step from its procedure.
                customProcedureId: step.customProcedureId ?? null,
                isFeeEdited: step.isFeeEdited,
                toothSurfaces: step.toothSurfaces ?? [],
                codeSuggestions: codeSuggestionsForPayload(
                  step.customProcedureId,
                  step.codeSuggestions,
                ).map((suggestion) => ({
                  cdtCodeId: suggestion.cdtCodeId,
                })),
              };
              if (step.id > 0) {
                stepPayload.id = step.id;
              }
              return stepPayload;
            }),
        };
        if (appointment.id > 0) {
          payload.id = appointment.id;
        }
        return payload;
      });

      await saveTreatmentPlanUpdate(
        treatmentPlan.status,
        async (saveStatus, hasAppointmentUpdates) => {
          const updated = await treatmentPlanService.update(planId, {
            name: treatmentPlan.name,
            treatmentSummary: treatmentPlan.treatmentSummary,
            providerNote: treatmentPlan.providerNote,
            fee: treatmentPlan.fee || undefined,
            patientSummary: treatmentPlan.patientSummary || undefined,
            status: saveStatus,
            appointments: appointmentPayload,
            hasAppointmentUpdates,
          });

          setTreatmentPlan(updated);
          setAppointments(
            [...updated.appointments].sort((a, b) => a.sortOrder - b.sortOrder),
          );
        },
        { hasAppointmentUpdates: true },
      );
    },
    [planId, setTreatmentPlan, treatmentPlan],
  );

  const handleStartAppointmentEdit = useCallback(
    (appointment: TreatmentPlanAppointment) => {
      if (!canEditAppointments) return;
      if (
        editingAppointmentId !== null &&
        editingAppointmentId !== appointment.id
      ) {
        return;
      }

      const editable = convertToEditableAppointment(appointment);
      const catalog = [...cdtCodes, ...peekOrganizationCdtCodes()];
      editable.procedures = editable.procedures.map((procedure) => {
        const { flagsByCode, capabilities } =
          resolveTreatAreaCapabilitiesForCodes(
            procedure.resolvedCdtCodes,
            procedure.codeTreatAreaFlagsByCode,
            catalog,
          );

        let teeth = procedure.teeth;
        let surfaces = procedure.surfaces;
        if (!canSelectTeeth(capabilities)) {
          teeth = [];
        }
        if (!canSelectSurfaces(capabilities)) {
          surfaces = [];
        } else {
          surfaces = filterSurfacesForCapabilities(surfaces, capabilities);
        }
        if (hasPureAreaSelection(surfaces)) {
          teeth = [];
          surfaces = keepOnlyAreaSurfaces(surfaces);
        }

        return {
          ...procedure,
          teeth,
          surfaces,
          codeTreatAreaFlagsByCode: flagsByCode,
          treatAreaCapabilities: capabilities,
        };
      });

      setEditingAppointmentId(appointment.id);
      setAppointmentDrafts((prev) => ({
        ...prev,
        [appointment.id]: editable,
      }));
      setAppointmentInitialDrafts((prev) => ({
        ...prev,
        [appointment.id]: cloneEditableAppointment(editable),
      }));
    },
    [canEditAppointments, cdtCodes, editingAppointmentId],
  );

  const handleCancelAppointmentEdit = useCallback((appointmentId: number) => {
    const initialDraft = appointmentInitialDrafts[appointmentId];

    setAppointmentDrafts((prev) => {
      const next = { ...prev };
      delete next[appointmentId];
      return next;
    });
    setAppointmentInitialDrafts((prev) => {
      const next = { ...prev };
      delete next[appointmentId];
      return next;
    });
    setEditingAppointmentId(null);

    if (appointmentId < 0) {
      setAppointments((prev) =>
        prev.filter((appointment) => appointment.id !== appointmentId),
      );
      return;
    }

    if (initialDraft) {
      setAppointments((prev) =>
        prev.map((appointment) =>
          appointment.id === appointmentId
            ? restoreAppointmentFromInitialDraft(appointment, initialDraft)
            : appointment,
        ),
      );
    }
  }, [appointmentInitialDrafts]);

  const handleDraftAppointmentChange = useCallback(
    (
      appointmentId: number,
      field: keyof EditableAppointmentData,
      value: unknown,
    ) => {
      setAppointmentDrafts((prev) => {
        const draft = prev[appointmentId];
        if (!draft) return prev;

        const updated = { ...draft, [field]: value };
        if (field === "totalDurationMinutes") {
          updated.isDurationEdited = true;
        }
        if (field === "procedures") {
          updated.totalDurationMinutes = computeAppointmentDuration(
            value as EditableProcedureData[],
          );
          updated.isDurationEdited = false;
        }

        return {
          ...prev,
          [appointmentId]: updated,
        };
      });
    },
    [],
  );

  const handleDraftProcedureChange = useCallback(
    (
      appointmentId: number,
      procedureIndex: number,
      field: keyof EditableProcedureData,
      value: unknown,
    ) => {
      setAppointmentDrafts((prev) => {
        const draft = prev[appointmentId];
        if (!draft) return prev;
        const initialProcedure =
          appointmentInitialDrafts[appointmentId]?.procedures[procedureIndex];
        const procedures = draft.procedures.map((procedure, index) => {
          if (index !== procedureIndex) return procedure;

          if (field === "fee") {
            const updated: EditableProcedureData = {
              ...procedure,
              fee: sanitizeFeeInput(String(value)),
            };
            return syncProcedureIsFeeEdited(updated, initialProcedure);
          }

          if (field === "surfaces") {
            const payload = value as
              | string[]
              | { surfaces: string[]; clearTeeth?: boolean };
            const nextSurfaces = Array.isArray(payload)
              ? payload
              : payload.surfaces;
            const shouldClearTeeth =
              (!Array.isArray(payload) && Boolean(payload.clearTeeth)) ||
              hasPureAreaSelection(nextSurfaces);

            let updated: EditableProcedureData = {
              ...procedure,
              surfaces: nextSurfaces,
            };

            if (shouldClearTeeth) {
              updated.surfaces = keepOnlyAreaSurfaces(nextSurfaces);
              const previousTeeth = procedure.teeth;
              updated.teeth = [];
              const scaled = scaleFeeAndDurationForTeeth({
                fee: updated.fee,
                durationMinutes: updated.durationMinutes,
                previousTeeth,
                nextTeeth: [],
                previousSurfaces: procedure.surfaces,
                nextSurfaces: updated.surfaces,
                isFeeEdited: procedure.isFeeEdited,
                capabilities: procedure.treatAreaCapabilities,
              });
              updated.fee = scaled.fee;
              updated.durationMinutes = scaled.durationMinutes;
              // Keep isFeeEdited as-is — tooth/area scaling is not a manual fee edit.
              updated.isFeeEdited = procedure.isFeeEdited;
            } else {
              updated.surfaces = clearAreaSurfaces(nextSurfaces);
              const scaled = scaleFeeAndDurationForTeeth({
                fee: updated.fee,
                durationMinutes: updated.durationMinutes,
                previousTeeth: procedure.teeth,
                nextTeeth: procedure.teeth,
                previousSurfaces: procedure.surfaces,
                nextSurfaces: updated.surfaces,
                isFeeEdited: procedure.isFeeEdited,
                capabilities: procedure.treatAreaCapabilities,
              });
              updated.fee = scaled.fee;
              updated.durationMinutes = scaled.durationMinutes;
              updated.isFeeEdited = procedure.isFeeEdited;
            }
            return updated;
          }

          if (field === "teeth") {
            const nextTeeth = value as string[];
            let updated: EditableProcedureData = {
              ...procedure,
              teeth: nextTeeth,
            };
            if (hasPureAreaSelection(updated.surfaces)) {
              updated.surfaces = clearAreaSurfaces(updated.surfaces);
            }
            const scaled = scaleFeeAndDurationForTeeth({
              fee: procedure.fee,
              durationMinutes: procedure.durationMinutes,
              previousTeeth: procedure.teeth,
              nextTeeth,
              previousSurfaces: procedure.surfaces,
              nextSurfaces: updated.surfaces,
              isFeeEdited: procedure.isFeeEdited,
              capabilities: procedure.treatAreaCapabilities,
            });
            updated.fee = scaled.fee;
            updated.durationMinutes = scaled.durationMinutes;
            // Do not flip isFeeEdited when fee changes due to tooth/area count.
            updated.isFeeEdited = procedure.isFeeEdited;
            return updated;
          }

          const updated = { ...procedure, [field]: value };
          return syncProcedureIsFeeEdited(updated, initialProcedure);
        });

        const nextDraft: EditableAppointmentData = {
          ...draft,
          procedures,
        };

        if (field === "teeth" || field === "surfaces") {
          nextDraft.totalDurationMinutes =
            computeAppointmentDuration(procedures);
          nextDraft.isDurationEdited = false;
        }

        return {
          ...prev,
          [appointmentId]: nextDraft,
        };
      });
    },
    [appointmentInitialDrafts],
  );

  const handleSaveAppointmentEdit = useCallback(
    async (appointmentId: number, durationMinutes?: number) => {
      if (!canEditAppointments) return;

      const baseDraft = appointmentDraftsRef.current[appointmentId];
      if (!baseDraft) return;

      const draft =
        durationMinutes !== undefined
          ? {
              ...baseDraft,
              totalDurationMinutes: durationMinutes,
              isDurationEdited: true,
            }
          : baseDraft;

      if (durationMinutes !== undefined) {
        setAppointmentDrafts((prev) => ({
          ...prev,
          [appointmentId]: draft,
        }));
      }

      if (draft.procedures.some((procedure) => !procedure.text?.trim())) {
        return;
      }

      setSavingAppointmentId(appointmentId);

      try {
        const updatedAppointment: TreatmentPlanAppointment = {
          id: appointmentId,
          timeframeLabel: draft.timeframeValue || "",
          conditionalPath: draft.conditionalPath || "",
          description: draft.description || "",
          notes: "",
          sortOrder: draft.sortOrder,
          totalDurationMinutes: draft.totalDurationMinutes ?? 0,
          totalFee: draft.procedures.reduce(
            (sum, procedure) => sum + (parseFloat(procedure.fee || "0") || 0),
            0,
          ),
          steps: draft.procedures.map((procedure, index) => {
            // Mirror the API shape: bundle member codes carry a null id so they
            // are not persisted back as standalone codes.
            const memberCodes = new Set(bundleMemberCodes(procedure));

            return {
              // Only keep real step ids; omit id for new steps (id must be >= 1)
              id: procedure.id && procedure.id >= 1 ? procedure.id : 0,
              name: procedure.text || "",
              description: procedure.description || "",
              sortOrder: index + 1,
              durationMinutes: procedure.durationMinutes ?? 0,
              fee: parseFloat(procedure.fee || "0") || 0,
              customProcedureId: procedure.customProcedureId ?? null,
              isFeeEdited: procedure.isFeeEdited,
              toothSurfaces: toothSurfacesFromProcedure(procedure),
              codeSuggestions: procedure.resolvedCdtCodes
                .map((code, suggestionIndex) => {
                  const match = cdtCodes.find((item) => item.code === code);
                  if (!match) return null;
                  return {
                    id: memberCodes.has(code) ? null : suggestionIndex + 1,
                    cdtCodeId: match.id,
                    code: match.code,
                    description: match.description,
                    defaultFee: match.defaultFee,
                  };
                })
                .filter(
                  (
                    suggestion,
                  ): suggestion is {
                    id: number | null;
                    cdtCodeId: number;
                    code: string;
                    description: string;
                    defaultFee: number;
                  } => suggestion !== null,
                ),
            };
          }),
        };

        const nextAppointments = appointments.map((appointment) =>
          appointment.id === appointmentId ? updatedAppointment : appointment,
        );

        await updatePlanAppointments(nextAppointments);
        handleCancelAppointmentEdit(appointmentId);
        showNotification(t("treatmentPlan.appointmentSaved"));
      } catch (saveError) {
        console.error("Error saving appointment changes:", saveError);
        notifySaveError(saveError);
      } finally {
        setSavingAppointmentId(null);
      }
    },
    [
      appointments,
      canEditAppointments,
      cdtCodes,
      handleCancelAppointmentEdit,
      notifySaveError,
      showNotification,
      t,
      updatePlanAppointments,
    ],
  );

  const handleDeleteAppointment = useCallback(
    async (appointmentId: number) => {
      if (!canEditAppointments) return;
      if (editingAppointmentId !== null) return;

      const remaining = appointments
        .filter((appointmentItem) => appointmentItem.id !== appointmentId)
        .map((appointmentItem, appointmentIndex) => ({
          ...appointmentItem,
          sortOrder: appointmentIndex + 1,
        }));

      setDeletingAppointmentId(appointmentId);

      try {
        await updatePlanAppointments(remaining);
        showNotification(t("treatmentPlan.appointmentDeleted"));
      } catch (deleteError) {
        console.error("Failed to delete appointment:", deleteError);
        notifySaveError(deleteError);
        await onRefetchPlan(true);
      } finally {
        setDeletingAppointmentId(null);
      }
    },
    [
      appointments,
      canEditAppointments,
      editingAppointmentId,
      onRefetchPlan,
      notifySaveError,
      showNotification,
      t,
      updatePlanAppointments,
    ],
  );

  const handleAddAppointment = useCallback(() => {
    if (!canEditAppointments) return;
    if (editingAppointmentId !== null) return;

    const newId = -Date.now();
    const newAppointment: TreatmentPlanAppointment = {
      id: newId,
      timeframeLabel: "",
      conditionalPath: "",
      description: "",
      notes: "",
      sortOrder: appointments.length + 1,
      totalDurationMinutes: 0,
      totalFee: 0,
      steps: [],
    };
    setAppointments((prev) => [...prev, newAppointment]);
    handleStartAppointmentEdit(newAppointment);
  }, [
    appointments.length,
    canEditAppointments,
    editingAppointmentId,
    handleStartAppointmentEdit,
  ]);

  const handleDraftCdtChange = useCallback(
    (
      appointmentId: number,
      procedureIndex: number,
      value: CDTAndCustomProcedureValue,
      allCdtCodes: string[],
      allDescriptions: string[],
      totalFee: number,
      totalDurationMinutes: number,
    ) => {
      setAppointmentDrafts((prev) => {
        const draft = prev[appointmentId];
        if (!draft) return prev;

        const initialProcedure =
          appointmentInitialDrafts[appointmentId]?.procedures[procedureIndex];

        const procedures = draft.procedures.map((procedure, index) => {
          if (index !== procedureIndex) return procedure;

          const { flagsByCode, capabilities } =
            resolveTreatAreaCapabilitiesForCodes(
              allCdtCodes,
              procedure.codeTreatAreaFlagsByCode,
              [...cdtCodes, ...peekOrganizationCdtCodes()],
            );

          const nextProcedure =
            allCdtCodes.length === 0
              ? procedureAfterCdtCodesCleared({
                  ...procedure,
                  cdtCodes: [],
                  customProcedureId: undefined,
                  resolvedCdtCodes: [],
                  explanation: "",
                })
              : syncProcedureAfterCdtChange(
                  {
                    ...procedure,
                    cdtCodes: value.cdtCodes,
                    customProcedureId: value.customProcedureId,
                    resolvedCdtCodes: allCdtCodes,
                    explanation: allDescriptions.filter(Boolean).join("\n"),
                    fee: sanitizeFeeInput(
                      String(
                        applyToothMultiplier(
                          totalFee,
                          procedure.teeth,
                          capabilities,
                          procedure.surfaces,
                        ),
                      ),
                    ),
                    durationMinutes: applyToothMultiplier(
                      totalDurationMinutes,
                      procedure.teeth,
                      capabilities,
                      procedure.surfaces,
                    ),
                    codeTreatAreaFlagsByCode: flagsByCode,
                    treatAreaCapabilities: capabilities,
                  },
                  initialProcedure,
                );

          const hasCdtCodes = nextProcedure.resolvedCdtCodes.length > 0;
          if (!hasCdtCodes) {
            return procedureAfterCdtCodesCleared(nextProcedure);
          }

          let teeth = nextProcedure.teeth;
          let surfaces = nextProcedure.surfaces;
          if (!canSelectTeeth(capabilities)) {
            teeth = [];
          }
          if (!canSelectSurfaces(capabilities)) {
            surfaces = [];
          } else {
            surfaces = filterSurfacesForCapabilities(surfaces, capabilities);
          }

          if (
            teeth === nextProcedure.teeth &&
            surfaces === nextProcedure.surfaces
          ) {
            return nextProcedure;
          }

          const scaled = scaleFeeAndDurationForTeeth({
            fee: nextProcedure.fee,
            durationMinutes: nextProcedure.durationMinutes,
            previousTeeth: nextProcedure.teeth,
            nextTeeth: teeth,
            previousSurfaces: nextProcedure.surfaces,
            nextSurfaces: surfaces,
            isFeeEdited: nextProcedure.isFeeEdited,
            capabilities,
          });

          return {
            ...nextProcedure,
            teeth,
            surfaces,
            fee: scaled.fee,
            durationMinutes: scaled.durationMinutes,
            isFeeEdited: nextProcedure.isFeeEdited,
          };
        });

        return {
          ...prev,
          [appointmentId]: {
            ...draft,
            procedures,
            totalDurationMinutes: computeAppointmentDuration(procedures),
            isDurationEdited: false,
          },
        };
      });
    },
    [appointmentInitialDrafts, cdtCodes],
  );

  const handleAppointmentDragStart = useCallback(
    (event: DragEvent, index: number) => {
      if (!canEditAppointments) return;
      if (hasActiveEditSessions) return;
      if (isDeletingAppointment) return;

      dragItem.current = index;

      const dragHandle = event.currentTarget as HTMLElement;
      const appointmentWrapper = dragHandle.closest(
        "[data-appointment-wrapper]",
      ) as HTMLElement | null;
      if (appointmentWrapper) {
        const wrapperRect = appointmentWrapper.getBoundingClientRect();
        const offsetX = event.clientX - wrapperRect.left;
        const offsetY = event.clientY - wrapperRect.top;

        const dragImage = appointmentWrapper.cloneNode(true) as HTMLElement;
        dragImage.style.width = `${wrapperRect.width}px`;
        dragImage.style.position = "absolute";
        dragImage.style.top = "-9999px";
        dragImage.style.left = "0";
        dragImage.style.opacity = "1";
        dragImage.style.pointerEvents = "none";
        document.body.appendChild(dragImage);

        event.dataTransfer.setDragImage(dragImage, offsetX, offsetY);
        requestAnimationFrame(() => {
          document.body.removeChild(dragImage);
        });
      }

      setDraggingAppointmentIndex(index);
    },
    [canEditAppointments, hasActiveEditSessions, isDeletingAppointment],
  );

  const handleAppointmentDragOver = useCallback(
    (event: DragEvent, index: number) => {
      if (!canEditAppointments) return;
      if (hasActiveEditSessions) return;
      event.preventDefault();
      if (
        draggingAppointmentIndex !== null &&
        draggingAppointmentIndex !== index
      ) {
        dragOverItem.current = index;
        setDragOverAppointmentIndex(index);
      }
    },
    [canEditAppointments, draggingAppointmentIndex, hasActiveEditSessions],
  );

  const handleAppointmentDragLeave = useCallback((event: DragEvent) => {
    const relatedTarget = event.relatedTarget as Node | null;
    if (!event.currentTarget.contains(relatedTarget)) {
      setDragOverAppointmentIndex(null);
    }
  }, []);

  const handleAppointmentDragEnd = useCallback(() => {
    dragItem.current = null;
    dragOverItem.current = null;
    setDraggingAppointmentIndex(null);
    setDragOverAppointmentIndex(null);
  }, []);

  const handleAppointmentDrop = useCallback(
    async (event: DragEvent, dropIndex: number) => {
      if (!canEditAppointments) return;
      if (hasActiveEditSessions) return;
      event.preventDefault();

      const draggedIndex = dragItem.current;
      if (draggedIndex === null || draggedIndex === dropIndex) {
        dragItem.current = null;
        dragOverItem.current = null;
        setDraggingAppointmentIndex(null);
        setDragOverAppointmentIndex(null);
        return;
      }

      const reordered = [...appointments];
      const [dragged] = reordered.splice(draggedIndex, 1);
      reordered.splice(dropIndex, 0, dragged);
      setAppointments(
        reordered.map((appointment, index) => ({
          ...appointment,
          sortOrder: index + 1,
        })),
      );
      setDraggingAppointmentIndex(null);
      setDragOverAppointmentIndex(null);
      dragItem.current = null;
      dragOverItem.current = null;

      try {
        await updatePlanAppointments(reordered);
        showNotification(t("treatmentPlan.appointmentOrderUpdated"));
      } catch (reorderError) {
        console.error("Failed to persist appointment order:", reorderError);
        notifySaveError(reorderError);
        await onRefetchPlan(true);
      }
    },
    [
      appointments,
      canEditAppointments,
      hasActiveEditSessions,
      onRefetchPlan,
      notifySaveError,
      showNotification,
      t,
      updatePlanAppointments,
    ],
  );

  const getAppointmentDraft = useCallback(
    (appointment: TreatmentPlanAppointment) =>
      appointmentDrafts[appointment.id] ||
      convertToEditableAppointment(appointment),
    [appointmentDrafts],
  );

  const getAppointmentHasChanges = useCallback(
    (
      appointment: TreatmentPlanAppointment,
      isLastAppointment: boolean,
    ) => {
      const draft = getAppointmentDraft(appointment);
      const initialDraft = appointmentInitialDrafts[appointment.id];
      return initialDraft
        ? hasAppointmentDraftChanges(draft, initialDraft, isLastAppointment)
        : false;
    },
    [appointmentInitialDrafts, getAppointmentDraft],
  );

  return {
    appointments,
    syncAppointmentsFromPlan,
    sortedAppointments,
    hasOptions,
    editingAppointmentId,
    savingAppointmentId,
    deletingAppointmentId,
    draggingAppointmentIndex,
    dragOverAppointmentIndex,
    isAppointmentInteractionBlocked,
    handleStartAppointmentEdit,
    handleCancelAppointmentEdit,
    handleDraftAppointmentChange,
    handleDraftProcedureChange,
    handleSaveAppointmentEdit,
    handleDeleteAppointment,
    handleAddAppointment,
    handleDraftCdtChange,
    handleAppointmentDragStart,
    handleAppointmentDragOver,
    handleAppointmentDragLeave,
    handleAppointmentDragEnd,
    handleAppointmentDrop,
    getAppointmentDraft,
    getAppointmentHasChanges,
  };
}
